"use client";

import { useEffect, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  gregorianFromRd,
  isoFromRd,
  parseGregorianIso,
  rdFromGregorian,
  utcDateFromRd,
} from "@/lib/tool-kit/calendars/rd";
import {
  ANIMALS,
  BRANCHES,
  chineseNewYearRd,
  FIRST_ZODIAC_YEAR,
  LAST_ZODIAC_YEAR,
  STEMS,
  yearsOfAnimal,
  zodiacOfYear,
  zodiacYearOfRd,
} from "@/lib/tool-kit/calendars/zodiac";

/** Animal and element characters. Data, not prose. */
const ANIMAL_CHARACTERS: Record<(typeof ANIMALS)[number], string> = {
  rat: "鼠",
  ox: "牛",
  tiger: "虎",
  rabbit: "兔",
  dragon: "龙",
  snake: "蛇",
  horse: "马",
  goat: "羊",
  monkey: "猴",
  rooster: "鸡",
  dog: "狗",
  pig: "猪",
};

const ELEMENT_CHARACTERS = {
  wood: "木",
  fire: "火",
  earth: "土",
  metal: "金",
  water: "水",
} as const;

const TABLE_FIRST_YEAR = 1948;
const TABLE_LAST_YEAR = 2031;

/** English strings. A translated content file overrides these through `ui`. */
export const CHINESE_ZODIAC_CALCULATOR_STRINGS = {
  inputKind: "Find the sign by",
  kindDate: "Birth date",
  kindYear: "Lunar year",
  birthDate: "Birth date (Gregorian)",
  lunarYear: "Lunar year",
  today: "Today",
  animals: {
    rat: "Rat",
    ox: "Ox",
    tiger: "Tiger",
    rabbit: "Rabbit",
    dragon: "Dragon",
    snake: "Snake",
    horse: "Horse",
    goat: "Goat",
    monkey: "Monkey",
    rooster: "Rooster",
    dog: "Dog",
    pig: "Pig",
  },
  elements: {
    wood: "Wood",
    fire: "Fire",
    earth: "Earth",
    metal: "Metal",
    water: "Water",
  },
  yang: "Yang",
  yin: "Yin",
  title: "{element} {animal}",
  animalLabel: "Animal",
  elementLabel: "Element",
  polarityLabel: "Polarity",
  stemBranchLabel: "Stem and branch",
  cycleLabel: "Place in the 60-year cycle",
  cycleValue: "{position} of 60",
  yearLabel: "Zodiac year",
  before:
    "{date} is before Chinese New Year in {gregorian}, which fell on {newYear}. It belongs to the zodiac year that began in {year}.",
  after:
    "{date} is on or after Chinese New Year in {gregorian}, which fell on {newYear}. It belongs to the zodiac year that began in {year}.",
  span: "The zodiac year {year} runs from {start} to {end}.",
  springStart:
    "Some astrologers, such as Bazi (Four Pillars) practitioners, start the year at the Start of Spring, about 4 February, instead. By that rule this date falls in {year}, the year of the {element} {animal}.",
  rangeNote:
    "Chinese New Year is the second new moon after the winter solstice (occasionally the third), at Beijing time. The table in this tool covers 1900 to 2101.",
  tableHeading: "The twelve animals and their years",
  animalColumn: "Animal",
  yearsColumn: "Years (from Chinese New Year)",
  pickDate: "Pick a date to find the sign.",
  pickYear: "Enter a lunar year.",
  invalidDate: "Choose a real date from 1 January 1900 to 31 December 2100.",
  invalidYear: "Enter a year from 1 to 9999.",
  yearOnlyNote:
    "A year number names the zodiac year that starts at Chinese New Year of that Gregorian year. Dates in January and February can belong to the year before.",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{date}: {element} {animal} ({stemBranch})",
} as const;

type Strings = typeof CHINESE_ZODIAC_CALCULATOR_STRINGS;
type InputKind = "date" | "year";
type Reading =
  | { kind: "date"; rd: number; gregorianYear: number }
  | { kind: "year"; year: number }
  | { error: string }
  | null;

const LONG: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
};

function todayRd(): number {
  const now = new Date();
  return rdFromGregorian(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

function readDate(text: string, strings: Strings): Reading {
  if (text.trim() === "") {
    return null;
  }
  const rd = parseGregorianIso(text);
  const first = rdFromGregorian(FIRST_ZODIAC_YEAR, 1, 1);
  const last = rdFromGregorian(LAST_ZODIAC_YEAR, 12, 31);
  if (rd === null || rd < first || rd > last) {
    return { error: strings.invalidDate };
  }
  return { kind: "date", rd, gregorianYear: gregorianFromRd(rd).year };
}

function readYear(text: string, strings: Strings): Reading {
  if (text.trim() === "") {
    return null;
  }
  const year = parseIntegerInput(text);
  if (year === null || year < 1 || year > 9999) {
    return { error: strings.invalidYear };
  }
  return { kind: "year", year };
}

function useLabels(strings: Strings) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return {
    date: (rd: number) => formatDateTime(locale, utcDateFromRd(rd), LONG),
    year: (year: number) => fmt(year, { useGrouping: false }),
    title: (year: number) => {
      const zodiac = zodiacOfYear(year);
      return t(strings.title, {
        element: strings.elements[zodiac.element],
        animal: strings.animals[zodiac.animal],
      });
    },
  };
}

function Details({
  rd,
  gregorianYear,
  zodiacYear,
  strings,
}: {
  rd: number;
  gregorianYear: number;
  zodiacYear: number;
  strings: Strings;
}) {
  const labels = useLabels(strings);
  const start = chineseNewYearRd(zodiacYear) ?? 0;
  const next = chineseNewYearRd(zodiacYear + 1);
  const newYear = chineseNewYearRd(gregorianYear) ?? 0;
  const lines = [
    t(rd >= newYear ? strings.after : strings.before, {
      date: labels.date(rd),
      gregorian: labels.year(gregorianYear),
      newYear: labels.date(newYear),
      year: labels.year(zodiacYear),
    }),
  ];
  if (next !== null && start > 0) {
    lines.push(
      t(strings.span, {
        year: labels.year(zodiacYear),
        start: labels.date(start),
        end: labels.date(next - 1),
      })
    );
  }
  const springYear =
    rd >= rdFromGregorian(gregorianYear, 2, 4)
      ? gregorianYear
      : gregorianYear - 1;
  if (springYear !== zodiacYear) {
    const spring = zodiacOfYear(springYear);
    lines.push(
      t(strings.springStart, {
        year: labels.year(springYear),
        element: strings.elements[spring.element],
        animal: strings.animals[spring.animal],
      })
    );
  }
  return (
    <ul className="space-y-1 text-foreground text-sm leading-relaxed">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

function AnimalTable({
  highlight,
  strings,
}: {
  highlight: number;
  strings: Strings;
}) {
  const labels = useLabels(strings);
  const rows = ANIMALS.map((animal, index) => [
    `${ANIMAL_CHARACTERS[animal]} ${strings.animals[animal]}${index === highlight ? " ◀" : ""}`,
    yearsOfAnimal(index, TABLE_FIRST_YEAR, TABLE_LAST_YEAR)
      .map(labels.year)
      .join(", "),
  ]);
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <DataTable
        head={[strings.animalColumn, strings.yearsColumn]}
        rows={rows}
      />
      <p className="text-muted-foreground text-sm">{strings.rangeNote}</p>
    </section>
  );
}

function ResultPanel({
  reading,
  strings,
}: {
  reading:
    | { kind: "date"; rd: number; gregorianYear: number }
    | { kind: "year"; year: number };
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const labels = useLabels(strings);
  let zodiacYear: number;
  if (reading.kind === "year") {
    zodiacYear = reading.year;
  } else {
    zodiacYear =
      zodiacYearOfRd(reading.rd, reading.gregorianYear) ??
      reading.gregorianYear;
  }
  const zodiac = zodiacOfYear(zodiacYear);
  const [stemName, stemChar] = STEMS[zodiac.stem] ?? ["", ""];
  const [branchName, branchChar] = BRANCHES[zodiac.branch] ?? ["", ""];
  const stemBranch = `${stemName}-${branchName} (${stemChar}${branchChar})`;
  const title = labels.title(zodiacYear);
  return (
    <>
      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>
          {ANIMAL_CHARACTERS[zodiac.animal]} {title}
        </h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.animalLabel}
            value={strings.animals[zodiac.animal]}
          />
          <StatCard
            label={strings.elementLabel}
            value={`${ELEMENT_CHARACTERS[zodiac.element]} ${strings.elements[zodiac.element]}`}
          />
          <StatCard
            label={strings.polarityLabel}
            value={zodiac.yang ? strings.yang : strings.yin}
          />
          <StatCard label={strings.stemBranchLabel} value={stemBranch} />
          <StatCard
            label={strings.cycleLabel}
            value={t(strings.cycleValue, {
              position: fmt(zodiac.cyclePosition),
            })}
          />
          <StatCard label={strings.yearLabel} value={labels.year(zodiacYear)} />
        </StatGrid>
        {reading.kind === "date" ? (
          <Details
            gregorianYear={reading.gregorianYear}
            rd={reading.rd}
            strings={strings}
            zodiacYear={zodiacYear}
          />
        ) : (
          <p className="text-foreground text-sm">{strings.yearOnlyNote}</p>
        )}
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copySummary, {
            date:
              reading.kind === "date"
                ? labels.date(reading.rd)
                : labels.year(zodiacYear),
            element: strings.elements[zodiac.element],
            animal: strings.animals[zodiac.animal],
            stemBranch,
          })}
        />
      </section>
      <AnimalTable highlight={zodiac.animalIndex} strings={strings} />
    </>
  );
}

export default function ChineseZodiacCalculatorWidget() {
  const strings = useToolUi(CHINESE_ZODIAC_CALCULATOR_STRINGS);
  const [kind, setKind] = useState<InputKind>("date");
  const [date, setDate] = useState("");
  const [year, setYear] = useState("");

  useEffect(() => {
    const today = todayRd();
    setDate((current) => current || isoFromRd(today));
    setYear((current) => current || isoFromRd(today).slice(0, 4));
  }, []);

  const reading =
    kind === "date" ? readDate(date, strings) : readYear(year, strings);

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.inputKind}
        name="cz-kind"
        onChange={setKind}
        options={[
          { value: "date", label: strings.kindDate },
          { value: "year", label: strings.kindYear },
        ]}
        value={kind}
      />
      {kind === "date" ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <TextField
              id="cz-date"
              label={strings.birthDate}
              onChange={setDate}
              type="date"
              value={date}
            />
          </div>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setDate(isoFromRd(todayRd()))}
            type="button"
          >
            {strings.today}
          </button>
        </div>
      ) : (
        <TextField
          id="cz-year"
          label={strings.lunarYear}
          numeric
          onChange={setYear}
          value={year}
        />
      )}
      {reading === null ? (
        <Notice>{kind === "date" ? strings.pickDate : strings.pickYear}</Notice>
      ) : null}
      {reading !== null && "error" in reading ? (
        <ErrorNotice>{reading.error}</ErrorNotice>
      ) : null}
      {reading !== null && "kind" in reading ? (
        <ResultPanel reading={reading} strings={strings} />
      ) : null}
    </div>
  );
}
